/* =========================================================
   FILE: assets/eg-localization-popup.js
   Responsive localization popup
   SAFE ACCOUNT VERSION
   Logic:
   - first automatic popup uses Shopify browsing context as a soft recommendation
   - detected country is preselected only inside the popup
   - real storefront region changes only after submit
   - unavailable detected country falls back to US if available
   - desktop manual open uses current selected region and opens right region list
   - mobile manual open uses current selected region and native dropdown
   - repeated/manual region change can be opened from Account via eg:localization:open
   - mobile removes legacy menu localization UI
   - fixes mobile native select accessibility state
   - no dynamic account HTML injection
   - removes ?country=XX from URL after page load to prevent forced reset
   ========================================================= */

(() => {
  const STORAGE_KEY = 'egLocalizationPopupSeen';
  const OPEN_DELAY = 650;
  const OPEN_CLASS = 'is-open';
  const REGIONS_OPEN_CLASS = 'is-regions-open';
  const PAGE_LOCK_STATE_KEY = '__egLocalizationPopupPageLock';
  const MOBILE_QUERY = '(max-width: 989px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  const mobileMedia = window.matchMedia(MOBILE_QUERY);
  const reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);

  function isMobile() {
    return mobileMedia.matches;
  }

  function getPageLockState() {
    if (window[PAGE_LOCK_STATE_KEY]) {
      return window[PAGE_LOCK_STATE_KEY];
    }

    window[PAGE_LOCK_STATE_KEY] = {
      locked: false,
      popup: null,
      touchY: 0,
      onWheel: null,
      onTouchStart: null,
      onTouchMove: null,
      onKeydown: null
    };

    return window[PAGE_LOCK_STATE_KEY];
  }

  function canScrollElement(element, deltaY) {
    if (!(element instanceof HTMLElement)) return false;

    const maxScrollTop = Math.max(0, element.scrollHeight - element.clientHeight);

    if (maxScrollTop <= 1) return false;
    if (deltaY < 0) return element.scrollTop > 0;
    if (deltaY > 0) return element.scrollTop < maxScrollTop - 1;

    return false;
  }

  function getScrollableElement(target, boundary) {
    let element = target instanceof Element ? target : null;

    while (element && element !== document.body && element !== document.documentElement) {
      if (boundary && !boundary.contains(element)) return null;

      if (element instanceof HTMLElement) {
        const styles = window.getComputedStyle(element);
        const overflowY = styles.overflowY;
        const canOverflow = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';

        if (canOverflow && element.scrollHeight > element.clientHeight + 1) {
          return element;
        }
      }

      if (boundary && element === boundary) break;

      element = element.parentElement;
    }

    return null;
  }

  function shouldPreventDocumentScroll(event, deltaY) {
    const state = getPageLockState();
    const popup = state.popup;

    if (!state.locked || !popup || popup.hasAttribute('hidden')) return false;

    const target = event.target instanceof Element ? event.target : null;

    if (!target || !popup.contains(target)) return true;

    const scrollableElement = getScrollableElement(target, popup);

    if (!scrollableElement) return true;

    return !canScrollElement(scrollableElement, deltaY);
  }

  function lockPageScroll(popup) {
    const state = getPageLockState();

    if (state.locked) {
      state.popup = popup || state.popup;
      return;
    }

    state.locked = true;
    state.popup = popup || null;
    state.touchY = 0;

    state.onWheel = (event) => {
      if (shouldPreventDocumentScroll(event, event.deltaY || 0)) {
        event.preventDefault();
      }
    };

    state.onTouchStart = (event) => {
      const touch = event.touches && event.touches.length ? event.touches[0] : null;
      state.touchY = touch ? touch.clientY : 0;
    };

    state.onTouchMove = (event) => {
      const touch = event.touches && event.touches.length ? event.touches[0] : null;
      const nextY = touch ? touch.clientY : state.touchY;
      const deltaY = state.touchY ? state.touchY - nextY : 0;

      state.touchY = nextY;

      if (shouldPreventDocumentScroll(event, deltaY)) {
        event.preventDefault();
      }
    };

    state.onKeydown = (event) => {
      const scrollKeys = new Set([
        'ArrowUp',
        'ArrowDown',
        'PageUp',
        'PageDown',
        'Home',
        'End',
        ' '
      ]);

      if (!scrollKeys.has(event.key)) return;
      if (event.defaultPrevented) return;

      const target = event.target instanceof Element ? event.target : null;
      const popupElement = state.popup;

      if (!popupElement || !target || !popupElement.contains(target)) {
        event.preventDefault();
        return;
      }

      const deltaY = event.key === 'ArrowUp' || event.key === 'PageUp' || event.key === 'Home' ? -1 : 1;

      if (shouldPreventDocumentScroll(event, deltaY)) {
        event.preventDefault();
      }
    };

    document.addEventListener('wheel', state.onWheel, { passive: false, capture: true });
    document.addEventListener('touchstart', state.onTouchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', state.onTouchMove, { passive: false, capture: true });
    document.addEventListener('keydown', state.onKeydown, { capture: true });
  }

  function unlockPageScroll() {
    const state = getPageLockState();

    if (!state.locked) return;

    if (state.onWheel) {
      document.removeEventListener('wheel', state.onWheel, true);
    }

    if (state.onTouchStart) {
      document.removeEventListener('touchstart', state.onTouchStart, true);
    }

    if (state.onTouchMove) {
      document.removeEventListener('touchmove', state.onTouchMove, true);
    }

    if (state.onKeydown) {
      document.removeEventListener('keydown', state.onKeydown, true);
    }

    state.locked = false;
    state.popup = null;
    state.touchY = 0;
    state.onWheel = null;
    state.onTouchStart = null;
    state.onTouchMove = null;
    state.onKeydown = null;
  }

  function hasSeenPopup() {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function markPopupSeen() {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch (error) {
      /* localStorage may be blocked */
    }
  }

  function getShopifyRoot() {
    const root =
      window.Shopify &&
      window.Shopify.routes &&
      typeof window.Shopify.routes.root === 'string'
        ? window.Shopify.routes.root
        : '/';

    return root.endsWith('/') ? root : `${root}/`;
  }

  function getBrowsingContextSuggestionsUrl() {
    return `${getShopifyRoot()}browsing_context_suggestions.json?country[enabled]=true`;
  }

  function normalizeCountryCode(countryCode) {
    return String(countryCode || '').trim().toUpperCase();
  }

  function getDetectedCountry(data) {
    if (!data || typeof data !== 'object') return null;

    const detectedCountry = data.detected_values && data.detected_values.country;

    if (detectedCountry && typeof detectedCountry === 'object') {
      const code =
        detectedCountry.handle ||
        detectedCountry.iso_code ||
        detectedCountry.code;

      const name = detectedCountry.name;

      if (code && name) {
        return {
          code: normalizeCountryCode(code),
          name: String(name).trim()
        };
      }
    }

    const suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];

    for (const suggestion of suggestions) {
      const country =
        suggestion &&
        suggestion.parts &&
        suggestion.parts.country &&
        typeof suggestion.parts.country === 'object'
          ? suggestion.parts.country
          : null;

      if (!country) continue;

      const code = country.handle || country.iso_code || country.code;
      const name = country.name;

      if (code && name) {
        return {
          code: normalizeCountryCode(code),
          name: String(name).trim()
        };
      }
    }

    return null;
  }

  function getCleanReturnTo() {
    const url = new URL(window.location.href);

    url.searchParams.delete('country');

    const query = url.searchParams.toString();

    return `${url.pathname}${query ? `?${query}` : ''}${url.hash || ''}`;
  }

  function removeCountryParamFromAddressBar() {
    if (!window.history || typeof window.history.replaceState !== 'function') return;

    const url = new URL(window.location.href);

    if (!url.searchParams.has('country')) return;

    url.searchParams.delete('country');

    const query = url.searchParams.toString();
    const cleanUrl = `${url.pathname}${query ? `?${query}` : ''}${url.hash || ''}`;

    window.history.replaceState(window.history.state, document.title, cleanUrl);
  }

  function removeLegacyMobileLocalization() {
    document.querySelectorAll('.eg-m-localization').forEach((element) => {
      element.remove();
    });
  }

  function movePopupToBody(popup) {
    if (!popup || popup.parentElement === document.body) return;

    document.body.appendChild(popup);
  }

  function getPopupByTrigger(trigger) {
    const targetId = trigger ? trigger.getAttribute('data-eg-loc-popup-target') : '';

    if (targetId) {
      const popupById = document.getElementById(targetId);
      if (popupById) return popupById;
    }

    return document.querySelector('[data-eg-loc-popup]');
  }

  function updateHeaderTriggers(popup, isOpen) {
    if (!popup || !popup.id) return;

    document.querySelectorAll('[data-eg-open-loc-popup]').forEach((trigger) => {
      const targetId = trigger.getAttribute('data-eg-loc-popup-target');

      if (!targetId || targetId === popup.id) {
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }
    });
  }

  function getFocusableElements(container) {
    if (!container) return [];

    return Array.from(
      container.querySelectorAll(
        [
          'a[href]',
          'button:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])'
        ].join(',')
      )
    ).filter((element) => {
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }

  function initPopup(popup) {
    if (!popup || popup.dataset.egLocPopupReady === 'true') return;

    popup.dataset.egLocPopupReady = 'true';
    movePopupToBody(popup);

    const dialog = popup.querySelector('.eg-loc-popup__dialog');
    const form = popup.querySelector('.eg-loc-popup__form');
    const returnToInput = form ? form.querySelector('input[name="return_to"]') : null;
    const closeButtons = popup.querySelectorAll('[data-eg-loc-popup-close]');
    const regionsToggle = popup.querySelector('[data-eg-loc-regions-toggle]');
    const countryInput = popup.querySelector('[data-eg-loc-country-input]');
    const countrySelect = popup.querySelector('[data-eg-loc-country-select]');
    const regionOptions = Array.from(
      popup.querySelectorAll('[data-eg-loc-region-option]')
    );
    const currentName = popup.querySelector('[data-eg-loc-current-name]');
    const currentCurrency = popup.querySelector('[data-eg-loc-current-currency]');
    const title = popup.querySelector('[data-eg-loc-title]');
    const selectedLabel = popup.querySelector('[data-eg-loc-selected-label]');
    const submitButton = popup.querySelector('[data-eg-loc-submit]');
    const description = popup.querySelector('[data-eg-loc-description]');

    const defaultCountryCode = normalizeCountryCode(
      popup.getAttribute('data-eg-loc-default-country-code') || 'US'
    );

    let lastFocusedElement = null;
    let openTimer = null;
    let closeTimer = null;
    let popupMode = 'automatic';
    let detectedRecommendedCode = '';
    let userChangedCountry = false;
    let detectionRequestId = 0;

    function setText(element, text) {
      if (!element) return;

      element.textContent = text;
    }

    function getOptionName(option) {
      if (!option) return '';

      return (
        option.getAttribute('data-country-name') ||
        option.dataset.countryName ||
        option.textContent ||
        ''
      ).trim();
    }

    function getOptionCurrency(option) {
      if (!option) return '';

      return (
        option.getAttribute('data-country-currency') ||
        option.dataset.countryCurrency ||
        ''
      ).trim();
    }

    function updateNativeSelectAccessibility() {
      if (!countrySelect) return;

      if (isMobile()) {
        countrySelect.removeAttribute('aria-hidden');
        countrySelect.removeAttribute('tabindex');
        return;
      }

      if (document.activeElement === countrySelect) {
        countrySelect.blur();

        if (
          !popup.hasAttribute('hidden') &&
          dialog instanceof HTMLElement &&
          typeof dialog.focus === 'function'
        ) {
          dialog.focus({ preventScroll: true });
        }
      }

      countrySelect.setAttribute('aria-hidden', 'true');
      countrySelect.setAttribute('tabindex', '-1');
    }

    function updateReturnToInput() {
      if (!returnToInput) return;

      returnToInput.value = getCleanReturnTo();
    }

    function getSelectedOptionByCode(countryCode) {
      if (!countrySelect) return null;

      const normalizedCode = normalizeCountryCode(countryCode);

      return (
        Array.from(countrySelect.options).find((option) => {
          return normalizeCountryCode(option.value) === normalizedCode;
        }) || null
      );
    }

    function getCurrentSelectedOption() {
      if (!countrySelect) return null;

      return getSelectedOptionByCode(countrySelect.value);
    }

    function updateCountryInput(countryCode) {
      if (!countryInput) return;

      countryInput.value = String(countryCode || '').trim();
    }

    function updateSelectedCard(option) {
      if (!option) return;

      if (currentName) {
        currentName.textContent = getOptionName(option);
      }

      if (currentCurrency) {
        currentCurrency.textContent = getOptionCurrency(option);
      }
    }

    function updateRegionButtons(activeCode) {
      const normalizedCode = normalizeCountryCode(activeCode);

      regionOptions.forEach((button) => {
        const buttonCode = normalizeCountryCode(
          button.getAttribute('data-country-code') || ''
        );

        const isActive = buttonCode === normalizedCode;

        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    function syncFromSelect() {
      if (!countrySelect) return;

      const selectedOption = getSelectedOptionByCode(countrySelect.value);
      if (!selectedOption) return;

      updateCountryInput(selectedOption.value);
      updateSelectedCard(selectedOption);
      updateRegionButtons(selectedOption.value);
      updateReturnToInput();
    }

    function setSelectedCountry(countryCode, options = {}) {
      if (!countrySelect) return false;

      const selectedOption = getSelectedOptionByCode(countryCode);
      if (!selectedOption) return false;

      countrySelect.value = selectedOption.value;

      updateCountryInput(selectedOption.value);
      updateSelectedCard(selectedOption);
      updateRegionButtons(selectedOption.value);
      updateReturnToInput();

      if (options.closePanel !== false) {
        closeRegionsPanel();
      }

      return true;
    }

    function setRecommendedCopy(countryName) {
      const cleanCountryName = String(countryName || '').trim();

      if (!cleanCountryName) {
        setFallbackCopy();
        return;
      }

      setText(title, `Shopping from ${cleanCountryName}?`);
      setText(selectedLabel, 'Recommended region');
      setText(submitButton, `Continue with ${cleanCountryName}`);
      setText(
        description,
        'We use your region to show the correct currency, availability, and shopping experience.'
      );
    }

    function setFallbackCopy() {
      setText(title, 'Confirm your shopping region');
      setText(selectedLabel, 'Default region');
      setText(submitButton, 'Save region');
      setText(
        description,
        'Select your region to see the correct currency, availability, and shopping experience.'
      );
    }

    function setFirstVisitSelectedCopy() {
      setText(title, 'Confirm your shopping region');
      setText(selectedLabel, 'Selected region');
      setText(submitButton, 'Save region');
      setText(
        description,
        'Select your region to see the correct currency, availability, and shopping experience.'
      );
    }

    function setManualCopy() {
      setText(title, 'Change your shopping region');
      setText(selectedLabel, 'Current region');
      setText(submitButton, 'Save changes');
      setText(
        description,
        'Your region affects currency, availability, and the shopping experience.'
      );
    }

    function applyFallbackCountry() {
      if (!countrySelect) {
        setFallbackCopy();
        return;
      }

      const defaultOption = getSelectedOptionByCode(defaultCountryCode);
      const currentOption = getCurrentSelectedOption();

      if (defaultOption) {
        setSelectedCountry(defaultOption.value, {
          closePanel: false
        });
      } else if (currentOption) {
        setSelectedCountry(currentOption.value, {
          closePanel: false
        });
      }

      setFallbackCopy();
    }

    function applyAutomaticRecommendation() {
      if (!countrySelect) {
        setFallbackCopy();
        return;
      }

      const requestId = detectionRequestId + 1;
      detectionRequestId = requestId;

      setFallbackCopy();

      fetch(getBrowsingContextSuggestionsUrl(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Localization detection failed: ${response.status}`);
          }

          return response.json();
        })
        .then((data) => {
          if (requestId !== detectionRequestId) return;
          if (popupMode !== 'automatic') return;
          if (userChangedCountry) return;

          const detectedCountry = getDetectedCountry(data);

          if (!detectedCountry || !detectedCountry.code) {
            applyFallbackCountry();
            return;
          }

          const matchingOption = getSelectedOptionByCode(detectedCountry.code);

          if (!matchingOption) {
            applyFallbackCountry();
            return;
          }

          detectedRecommendedCode = normalizeCountryCode(matchingOption.value);

          setSelectedCountry(matchingOption.value, {
            closePanel: false
          });

          setRecommendedCopy(getOptionName(matchingOption) || detectedCountry.name);
        })
        .catch(() => {
          if (requestId !== detectionRequestId) return;
          if (popupMode !== 'automatic') return;
          if (userChangedCountry) return;

          applyFallbackCountry();
        });
    }

    function updateCopyAfterUserCountryChange(countryCode) {
      const selectedOption = getSelectedOptionByCode(countryCode);

      if (!selectedOption) return;

      if (popupMode === 'manual') {
        setManualCopy();
        return;
      }

      if (
        detectedRecommendedCode &&
        normalizeCountryCode(countryCode) === detectedRecommendedCode
      ) {
        setRecommendedCopy(getOptionName(selectedOption));
        return;
      }

      setFirstVisitSelectedCopy();
    }

    function openRegionsPanel() {
      if (isMobile()) return;

      popup.classList.add(REGIONS_OPEN_CLASS);

      if (regionsToggle) {
        regionsToggle.setAttribute('aria-expanded', 'true');
      }
    }

    function closeRegionsPanel() {
      popup.classList.remove(REGIONS_OPEN_CLASS);

      if (regionsToggle) {
        regionsToggle.setAttribute('aria-expanded', 'false');
      }
    }

    function toggleRegionsPanel() {
      if (isMobile()) return;

      if (popup.classList.contains(REGIONS_OPEN_CLASS)) {
        closeRegionsPanel();
        return;
      }

      openRegionsPanel();
    }

    function prepareAutomaticOpen() {
      popupMode = 'automatic';
      userChangedCountry = false;
      detectedRecommendedCode = '';

      closeRegionsPanel();
      syncFromSelect();
      applyAutomaticRecommendation();
    }

    function prepareManualOpen() {
      popupMode = 'manual';
      userChangedCountry = false;
      detectedRecommendedCode = '';
      detectionRequestId += 1;

      closeRegionsPanel();
      syncFromSelect();
      setManualCopy();
    }

    function openPopup(options = {}) {
      const force = options.force === true;
      const source = options.source === 'manual' ? 'manual' : 'automatic';

      if (!force && hasSeenPopup()) return;
      if (!popup.hasAttribute('hidden')) return;

      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);

      lastFocusedElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

      updateNativeSelectAccessibility();
      updateReturnToInput();

      if (source === 'manual') {
        prepareManualOpen();
      } else {
        prepareAutomaticOpen();
      }

      lockPageScroll(popup);

      popup.removeAttribute('hidden');
      updateHeaderTriggers(popup, true);

      requestAnimationFrame(() => {
        updateNativeSelectAccessibility();

        popup.classList.add(OPEN_CLASS);

        if (source === 'manual' && !isMobile()) {
          openRegionsPanel();
        }

        if (dialog instanceof HTMLElement) {
          dialog.focus({ preventScroll: true });
        }
      });
    }

    function closePopup(options = {}) {
      const shouldRemember = options.remember !== false;
      const shouldRestoreFocus = options.restoreFocus !== false;
      const closeDelay = reducedMotionMedia.matches ? 0 : isMobile() ? 220 : 140;

      if (popup.hasAttribute('hidden')) return;

      if (shouldRemember) {
        markPopupSeen();
      }

      closeRegionsPanel();
      popup.classList.remove(OPEN_CLASS);
      updateHeaderTriggers(popup, false);

      window.clearTimeout(closeTimer);

      closeTimer = window.setTimeout(() => {
        popup.setAttribute('hidden', '');
        unlockPageScroll();

        if (
          shouldRestoreFocus &&
          lastFocusedElement &&
          typeof lastFocusedElement.focus === 'function'
        ) {
          lastFocusedElement.focus({ preventScroll: true });
        }
      }, closeDelay);
    }

    function scheduleOpen() {
      window.clearTimeout(openTimer);

      if (hasSeenPopup()) return;

      openTimer = window.setTimeout(() => {
        openPopup({
          force: false,
          source: 'automatic'
        });
      }, OPEN_DELAY);
    }

    function handleKeydown(event) {
      if (popup.hasAttribute('hidden')) return;

      if (event.key === 'Escape') {
        event.preventDefault();

        if (popup.classList.contains(REGIONS_OPEN_CLASS)) {
          closeRegionsPanel();
          return;
        }

        closePopup({ remember: true });
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements(popup);
      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    closeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        closePopup({ remember: true });
      });
    });

    if (regionsToggle) {
      regionsToggle.addEventListener('click', (event) => {
        event.preventDefault();
        toggleRegionsPanel();
      });
    }

    if (countrySelect) {
      countrySelect.addEventListener('change', () => {
        userChangedCountry = true;
        syncFromSelect();
        updateCopyAfterUserCountryChange(countrySelect.value);
      });
    }

    regionOptions.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();

        const countryCode = button.getAttribute('data-country-code');
        if (!countryCode) return;

        userChangedCountry = true;

        if (setSelectedCountry(countryCode)) {
          updateCopyAfterUserCountryChange(countryCode);
        }
      });
    });

    document.addEventListener('keydown', handleKeydown);

    if (form) {
      form.addEventListener('submit', () => {
        removeCountryParamFromAddressBar();
        syncFromSelect();
        updateReturnToInput();

        markPopupSeen();
        form.classList.add('is-submitting');
        form.setAttribute('aria-busy', 'true');
      });
    }

    if (typeof mobileMedia.addEventListener === 'function') {
      mobileMedia.addEventListener('change', () => {
        closeRegionsPanel();
        updateNativeSelectAccessibility();
      });
    } else if (typeof mobileMedia.addListener === 'function') {
      mobileMedia.addListener(() => {
        closeRegionsPanel();
        updateNativeSelectAccessibility();
      });
    }

    popup.egOpenLocalizationPopup = () => {
      openPopup({
        force: true,
        source: 'manual'
      });
    };

    popup.egCloseLocalizationPopup = () => {
      closePopup({ remember: true });
    };

    popup.egOpenLocalizationRegions = openRegionsPanel;
    popup.egCloseLocalizationRegions = closeRegionsPanel;

    removeCountryParamFromAddressBar();
    updateNativeSelectAccessibility();
    updateReturnToInput();
    syncFromSelect();
    setFallbackCopy();
    scheduleOpen();
  }

  function openPopupFromEvent() {
    const popup = document.querySelector('[data-eg-loc-popup]');
    if (!popup) return;

    initPopup(popup);

    if (typeof popup.egOpenLocalizationPopup === 'function') {
      popup.egOpenLocalizationPopup();
    }
  }

  function initManualTriggers() {
    if (document.documentElement.dataset.egLocPopupTriggersReady === 'true') return;

    document.documentElement.dataset.egLocPopupTriggersReady = 'true';

    document.addEventListener('eg:localization:open', openPopupFromEvent);

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const trigger = target.closest('[data-eg-open-loc-popup]');
      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();

      const popup = getPopupByTrigger(trigger);
      if (!popup) return;

      initPopup(popup);

      if (typeof popup.egOpenLocalizationPopup === 'function') {
        popup.egOpenLocalizationPopup();
      }
    });
  }

  function init() {
    removeLegacyMobileLocalization();
    document.querySelectorAll('[data-eg-loc-popup]').forEach(initPopup);
    initManualTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
